// The web's copy of ParksphereMobileApp/utils/handoverProgress.js — what the owner reads about the
// person coming for their spot. Everything below this header must stay identical to the
// phone's; server/tests/sharedCopies.test.js checks it.
//
// The server relays an accepted requester's position as distance and ETA only, never coordinates.

// Older than this, the age is said out loud.
export const STALE_AFTER_MS = 60 * 1000;

const distanceText = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m / 50) * 50} m`);

const ageText = (ms) => {
  const min = Math.round(ms / 60000);
  return min < 1 ? 'under a minute ago' : `${min} min ago`;
};

const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * The accepted request's one line for the owner.
 * @param {{distanceM:number, etaMin:number, at:number}|null} progress  latest relayed reading
 * @param {number|null} waitDeadline  when they are expected by (ms), if known
 * @param {boolean} late  the server has said the deadline passed
 */
export function progressLine(progress, { waitDeadline = null, late = false, now = Date.now() } = {}) {
  const by = waitDeadline ? ` · expected by ${clock(waitDeadline)}` : '';
  if (!progress) return `${late ? 'Late' : 'On their way'}${by}`;
  const where = progress.distanceM <= 50
    ? 'At your spot'
    // Distance only (2026-11-01). A drive time here sat right above the deadline and read as a second,
    // contradicting clock ("1 min drive" over "3 min left", field) — the deadline is the one to show.
    : `${distanceText(progress.distanceM)} away`;
  const age = now - progress.at;
  const line = age > STALE_AFTER_MS ? `${where} · ${ageText(age)}` : where;
  return late ? `Late · ${line}` : line;
}

/**
 * The handover clock, the same for both sides (2026-11-01): "13:45 · 7 min left", "13:45 · due now",
 * or "13:45 · 3 min late" once the deadline has passed. Callers say whose: "Arrive by" to the driver,
 * "Expected by" to the owner. null without a deadline.
 */
export function countdownText(deadline, now = Date.now()) {
  if (!deadline) return null;
  const mins = Math.round((deadline - now) / 60000);
  const at = clock(deadline);
  if (mins > 0) return `${at} · ${mins} min left`;
  if (mins === 0) return `${at} · due now`;
  return `${at} · ${-mins} min late`;
}

// The requester's estimate BEFORE asking (2026-11-01): the same rule as the server's wait deadline
// (server/utils/handoverRules.js waitMinutesFor — server/tests/arrivalEstimate.test.js keeps the two
// equal): travel at 250 m/min, 15 km/h, plus 5 to find the street, never under 5. Owners hold a spot
// at most 30 minutes, so beyond that the estimate is said as it is and flagged rather than capped.
export const HOLD_MAX_MIN = 30;
export function arrivalEstimate(distanceM) {
  if (typeof distanceM !== 'number' || !(distanceM >= 0)) return null; // null >= 0 is true in JS
  const minutes = Math.max(5, Math.ceil(distanceM / 250) + 5);
  return { minutes, beyondHold: minutes > HOLD_MAX_MIN };
}
